import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import useGoogleSignIn from '../useGoogleSignIn';
import { getLoginRecaptchaKey } from '../utils';

export default function LoginForm({
    email, password, error, loading,
    onEmailChange, onPasswordChange, onForgot, onSso, onSubmit,
    onGoogleSuccess, onGoogleUnavailable = undefined, onRecaptchaError = undefined, googleTrackingParams,
}) {
    const recaptchaRef = useRef(null);
    const { signIn } = useGoogleSignIn(onGoogleSuccess, onGoogleUnavailable);

    const handleSubmit = e => {
        e.preventDefault();
        recaptchaRef.current.execute();
    };

    const handleGoogleClick = () => signIn(googleTrackingParams);

    const handleRecaptchaError = () => {
        if (recaptchaRef.current) recaptchaRef.current.reset();
        if (onRecaptchaError) onRecaptchaError();
    };

    return (
        <form className="registration-form" onSubmit={handleSubmit}>
            {error ? (
                <div className="error-msg boxPadding">
                    <span className="error-msg-text" dangerouslySetInnerHTML={{ __html: error }} />
                </div>
            ) : null}

            <button type="button" className="btn-google-login" onClick={handleGoogleClick} disabled={loading}>
                <span className="btn-google-login-text"><Translate id="STATIC.PAGES.LOGIN.GOOGLE_LOGIN" /></span>
            </button>

            <div className="divider-with-text">
                <div className="divider-line" />
                <div className="divider-text"><Translate id="STATIC.PAGES.LOGIN.OR" /></div>
                <div className="divider-line" />
            </div>

            <div className="form-group">
                <label htmlFor="login-email"><Translate id="STATIC.PAGES.LOGIN.EMAIL" /></label>
                <input
                    id="login-email" type="email" className="form-control" placeholder="name@company.com" autoFocus
                    value={email} onChange={e => onEmailChange(e.target.value)}
                />
            </div>

            <div className="form-group">
                <div className="password-label-container">
                    <label htmlFor="login-password"><Translate id="STATIC.PAGES.LOGIN.PASSWORD" /></label>
                    <label className="forgot-your-password">
                        <a href="#" onClick={e => { e.preventDefault(); onForgot(); }}>
                            <Translate id="STATIC.PAGES.LOGIN.FORGOT_PASSWORD" />
                        </a>
                    </label>
                </div>
                <input
                    id="login-password" type="password" className="form-control" placeholder="**********"
                    value={password} onChange={e => onPasswordChange(e.target.value)}
                />
            </div>

            <ReCAPTCHA
                ref={recaptchaRef} size="invisible" sitekey={getLoginRecaptchaKey()}
                onChange={token => onSubmit(token)}
                onErrored={handleRecaptchaError}
                onExpired={handleRecaptchaError}
            />

            <button type="submit" className="btn btn-block btn-singular btn-primary" disabled={loading}>
                <Translate id="STATIC.PAGES.LOGIN.LOG_IN_BUTTON" />
            </button>
            <a
                className="form-underlink pull-left" href="#"
                onClick={e => { e.preventDefault(); onSso(); }}
            >
                <Translate id="STATIC.PAGES.LOGIN.SSO_LINK" />
            </a>
        </form>
    );
}

LoginForm.propTypes = {
    email: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
    error: PropTypes.string.isRequired,
    loading: PropTypes.bool.isRequired,
    onEmailChange: PropTypes.func.isRequired,
    onPasswordChange: PropTypes.func.isRequired,
    onForgot: PropTypes.func.isRequired,
    onSso: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onGoogleSuccess: PropTypes.func.isRequired,
    onGoogleUnavailable: PropTypes.func,
    onRecaptchaError: PropTypes.func,
    googleTrackingParams: PropTypes.shape({}).isRequired,
};
